import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Shield, Lock, Smartphone, Key, CheckCircle2, XCircle, 
  AlertTriangle, RefreshCw, Copy, Eye, EyeOff, QrCode,
  Fingerprint, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Security() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [biometricCredentials, setBiometricCredentials] = useState([]);
  const [setupData, setSetupData] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [showBiometricDialog, setShowBiometricDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState([]);
  const [biometricSupported, setBiometricSupported] = useState(false);

  useEffect(() => {
    // Check if WebAuthn is supported
    setBiometricSupported(
      window.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === 'function'
    );
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      // Fetch 2FA status
      const twoFaRes = await fetch(`${API_URL}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (twoFaRes.ok) {
        setTwoFactorStatus(await twoFaRes.json());
      }

      // Fetch biometric credentials
      const bioRes = await fetch(`${API_URL}/api/auth/biometric/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (bioRes.ok) {
        const data = await bioRes.json();
        setBiometricCredentials(data.credentials || []);
      }
    } catch (error) {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [token]);

  // 2FA Functions
  const startSetup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
        setShowSetupDialog(true);
      } else {
        toast.error('Failed to start 2FA setup');
      }
    } catch (error) {
      toast.error('Setup failed');
    }
  };

  const verifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verificationCode })
      });

      if (response.ok) {
        toast.success('Two-factor authentication enabled!');
        setShowSetupDialog(false);
        setVerificationCode('');
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Verification failed');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: disableCode })
      });

      if (response.ok) {
        toast.success('Two-factor authentication disabled');
        setShowDisableDialog(false);
        setDisableCode('');
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to disable 2FA');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    } finally {
      setVerifying(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!backupCode || backupCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/regenerate-backup`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: backupCode })
      });

      if (response.ok) {
        const data = await response.json();
        setNewBackupCodes(data.backup_codes);
        setShowBackupDialog(false);
        setBackupCode('');
        toast.success('Backup codes regenerated');
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to regenerate codes');
      }
    } catch (error) {
      toast.error('Failed to regenerate codes');
    } finally {
      setVerifying(false);
    }
  };

  // Biometric Functions
  const startBiometricSetup = async () => {
    if (!biometricSupported) {
      toast.error('Biometric authentication is not supported on this device');
      return;
    }

    try {
      // Get registration options from server
      const optionsRes = await fetch(`${API_URL}/api/auth/biometric/register/options`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!optionsRes.ok) {
        throw new Error('Failed to get registration options');
      }

      const options = await optionsRes.json();

      // Convert base64url to ArrayBuffer
      const challenge = base64urlToBuffer(options.challenge);
      const userId = base64urlToBuffer(options.user.id);

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: options.rp,
          user: {
            id: userId,
            name: options.user.name,
            displayName: options.user.displayName
          },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          authenticatorSelection: {
            userVerification: options.authenticatorSelection.userVerification,
            residentKey: options.authenticatorSelection.residentKey
          },
          attestation: options.attestation
        }
      });

      if (!credential) {
        throw new Error('Credential creation cancelled');
      }

      // Prepare credential for server
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject)
        }
      };

      // Verify with server
      const verifyRes = await fetch(`${API_URL}/api/auth/biometric/register/verify?device_name=${encodeURIComponent(deviceName || 'My Device')}`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentialForServer)
      });

      if (verifyRes.ok) {
        toast.success('Biometric authentication registered!');
        setShowBiometricDialog(false);
        setDeviceName('');
        fetchStatus();
      } else {
        const error = await verifyRes.json();
        throw new Error(error.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Biometric setup error:', error);
      toast.error(error.message || 'Failed to setup biometric authentication');
    }
  };

  const deleteBiometricCredential = async (credentialId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/biometric/credentials/${credentialId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Biometric credential removed');
        fetchStatus();
      } else {
        toast.error('Failed to remove credential');
      }
    } catch (error) {
      toast.error('Failed to remove credential');
    }
  };

  // Helper functions for base64url conversion
  const base64urlToBuffer = (base64url) => {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - base64.length % 4) % 4);
    const binary = atob(base64 + padding);
    const buffer = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      view[i] = binary.charCodeAt(i);
    }
    return buffer;
  };

  const bufferToBase64url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="security-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            Security Settings
          </h1>
          <p className="text-muted-foreground mt-1">Manage your account security and authentication methods</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 2FA Status Card */}
        <Card className="overflow-hidden">
          <div className={`h-1 ${twoFactorStatus?.enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Two-Factor Authentication
            </CardTitle>
            <CardDescription>
              Add an extra layer of security with authenticator apps
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
              <div className="flex items-center gap-3">
                {twoFactorStatus?.enabled ? (
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </div>
                ) : (
                  <div className="h-10 w-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  </div>
                )}
                <div>
                  <p className="font-medium">
                    {twoFactorStatus?.enabled ? '2FA Enabled' : '2FA Not Enabled'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {twoFactorStatus?.enabled 
                      ? `Method: ${twoFactorStatus?.method?.toUpperCase() || 'TOTP'}`
                      : 'Protect your account with 2FA'
                    }
                  </p>
                </div>
              </div>
              <Badge variant={twoFactorStatus?.enabled ? 'default' : 'secondary'}>
                {twoFactorStatus?.enabled ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            {twoFactorStatus?.enabled ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Backup codes remaining</span>
                  <Badge variant="outline">{twoFactorStatus?.backup_codes_remaining || 0}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowBackupDialog(true)} className="flex-1">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate Codes
                  </Button>
                  <Button variant="destructive" onClick={() => setShowDisableDialog(true)} className="flex-1">
                    <XCircle className="h-4 w-4 mr-2" />
                    Disable 2FA
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={startSetup} className="w-full" data-testid="enable-2fa-btn">
                <Lock className="h-4 w-4 mr-2" />
                Enable Two-Factor Authentication
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Biometric Authentication Card */}
        <Card className="overflow-hidden">
          <div className={`h-1 ${biometricCredentials.length > 0 ? 'bg-green-500' : 'bg-blue-500'}`} />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5" />
              Biometric Authentication
            </CardTitle>
            <CardDescription>
              Use fingerprint, Face ID, or Windows Hello to sign in
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!biometricSupported ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Not Supported</AlertTitle>
                <AlertDescription>
                  Your browser or device doesn't support biometric authentication
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${biometricCredentials.length > 0 ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                      <Fingerprint className={`h-5 w-5 ${biometricCredentials.length > 0 ? 'text-green-500' : 'text-blue-500'}`} />
                    </div>
                    <div>
                      <p className="font-medium">
                        {biometricCredentials.length > 0 ? 'Biometrics Enabled' : 'Not Set Up'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {biometricCredentials.length} device(s) registered
                      </p>
                    </div>
                  </div>
                  <Badge variant={biometricCredentials.length > 0 ? 'default' : 'secondary'}>
                    {biometricCredentials.length > 0 ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {biometricCredentials.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm">Registered Devices</Label>
                    {biometricCredentials.map((cred) => (
                      <div key={cred.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">{cred.device_name || 'Unknown Device'}</p>
                          <p className="text-xs text-muted-foreground">
                            Added {new Date(cred.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteBiometricCredential(cred.credential_id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <Button onClick={() => setShowBiometricDialog(true)} className="w-full" data-testid="add-biometric-btn">
                  <Fingerprint className="h-4 w-4 mr-2" />
                  {biometricCredentials.length > 0 ? 'Add Another Device' : 'Set Up Biometric Login'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Encryption Status Card */}
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-cyan-500 to-teal-500" />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Data Encryption
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm">Encryption in Transit</span>
              </div>
              <Badge className="bg-green-500">TLS 1.3</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm">Encryption at Rest</span>
              </div>
              <Badge className="bg-green-500">AES-256</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm">PHI Field Encryption</span>
              </div>
              <Badge className="bg-green-500">Fernet</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Session Security Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Session Security
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <span className="text-sm">Token Expiration</span>
              <Badge variant="outline">24 hours</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <span className="text-sm">Algorithm</span>
              <Badge variant="outline">HS256</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <span className="text-sm">Auto-logout</span>
              <Badge className="bg-green-500">Enabled</Badge>
            </div>
          </CardContent>
        </Card>

        {/* New Backup Codes Display */}
        {newBackupCodes.length > 0 && (
          <Card className="border-green-500 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-500">
                <Key className="h-5 w-5" />
                New Backup Codes
              </CardTitle>
              <CardDescription>
                Save these codes in a secure location. They won't be shown again.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription>
                  Each code can only be used once. Store them safely.
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {newBackupCodes.map((code, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded font-mono text-sm">
                    {code}
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(code)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button 
                className="w-full mt-4" 
                variant="outline"
                onClick={() => copyToClipboard(newBackupCodes.join('\n'))}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy All Codes
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Setup 2FA Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-4">
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Manual entry code:</Label>
                <div className="flex items-center gap-2">
                  <Input value={setupData.secret} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(setupData.secret)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Enter code from your authenticator app</Label>
                <Input
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest"
                  maxLength={6}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>Cancel</Button>
            <Button onClick={verifySetup} disabled={verifying || verificationCode.length !== 6}>
              {verifying ? 'Verifying...' : 'Enable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>Enter a code from your authenticator app to confirm</DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>Disabling 2FA will make your account less secure</AlertDescription>
          </Alert>
          
          <div className="space-y-2">
            <Label>Verification Code</Label>
            <Input
              placeholder="000000"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-widest"
              maxLength={6}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={disable2FA} disabled={verifying || disableCode.length !== 6}>
              {verifying ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={showBackupDialog} onOpenChange={setShowBackupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Regenerate Backup Codes</DialogTitle>
            <DialogDescription>This will invalidate your existing backup codes</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            <Label>Enter code from your authenticator app</Label>
            <Input
              placeholder="000000"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-widest"
              maxLength={6}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackupDialog(false)}>Cancel</Button>
            <Button onClick={regenerateBackupCodes} disabled={verifying || backupCode.length !== 6}>
              {verifying ? 'Regenerating...' : 'Regenerate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Biometric Setup Dialog */}
      <Dialog open={showBiometricDialog} onOpenChange={setShowBiometricDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Biometric Authentication</DialogTitle>
            <DialogDescription>
              Register your fingerprint, Face ID, or security key
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-center py-6">
              <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center">
                <Fingerprint className="h-12 w-12 text-primary" />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Device Name (optional)</Label>
              <Input
                placeholder="e.g., MacBook Pro, iPhone"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
              />
            </div>
            
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                When prompted, use your device's biometric sensor or security key to register.
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBiometricDialog(false)}>Cancel</Button>
            <Button onClick={startBiometricSetup}>
              <Fingerprint className="h-4 w-4 mr-2" />
              Register Biometric
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
